import clone from 'ramda/es/clone';
import { ChestWallAlignmentSetting } from 'generated/graphql';
import type { StepConfiguration, ViewportConfigurationEntry } from './steppedHPTypes';
import { parseTempStepId } from './tempStepId';

/**
 * Gets a StepConfiguration by index from a stepped HP's steps array.
 * Returns null if index is out of bounds.
 */
export function getStepConfigurationByIndex(
  stepConfigurations: Nullable<StepConfiguration[]>,
  stepIndex: number
): StepConfiguration | null {
  if (!stepConfigurations || stepIndex < 0 || stepIndex >= stepConfigurations.length) {
    return null;
  }
  return stepConfigurations[stepIndex];
}

/** The default name for the first step in a stepped HP, so the default sequence reads Step 1, Step 2, Step 3. */
export const BASE_STEP_NAME = 'Step 1';

/** Prefix for client-generated step smids (not valid backend UUIDs). */
export const CLIENT_STEP_SMID_PREFIX = 'step-';

/** Returns true if the smid is a client-generated placeholder (not persisted to backend). */
export function isClientGeneratedSmid(smid: Nullable<string>): boolean {
  return smid != null && smid.startsWith(CLIENT_STEP_SMID_PREFIX);
}

/** Returns the sub-step total for a step, or 0 if it has no sub-steps. */
export function calculateSubStepTotal(step: Nullable<StepConfiguration>): number {
  return step?.sub_steps?.length ?? 0;
}

export const DEFAULT_CONFIG_DETAILS: StepConfiguration['config_details'] = {
  quadrant_view: false,
  cad_on: false,
  chest_wall_alignment: ChestWallAlignmentSetting.None,
};

/**
 * Creates a new step, optionally deep-cloning from a source step. When cloning, overrides smid and
 * display_name on the clone. When creating from scratch, uses the provided defaultLayout (defaults
 * to "2,2").
 */
export function createDefaultStep(options: {
  name: string;
  smid?: string | null;
  sourceStep?: StepConfiguration;
  defaultLayout?: string;
}): StepConfiguration {
  const {
    name,
    smid = `${CLIENT_STEP_SMID_PREFIX}${crypto.randomUUID()}`,
    sourceStep,
    defaultLayout = '2,2',
  } = options;

  if (sourceStep) {
    return {
      ...clone(sourceStep),
      smid,
      display_name: name,
    };
  }

  return {
    smid,
    display_name: name,
    layouts: { '0': defaultLayout },
    config_details: { ...DEFAULT_CONFIG_DETAILS },
    viewports: {},
    sub_steps: null,
  };
}

/**
 * Returns the default display name for a step about to be appended to `stepConfigurations`.
 *
 * Numbering starts at the positional name (`Step N+1`) and advances past any label already in use,
 * so a step renamed to a default-shaped label — e.g. a Base Step renamed to "Step 2" — can never be
 * duplicated by a later auto-generated name (VX-4620). Terminates because the set of taken labels
 * is finite.
 */
export function generateDefaultStepName(stepConfigurations: StepConfiguration[]): string {
  if (stepConfigurations.length === 0) return BASE_STEP_NAME;

  let stepNumber = stepConfigurations.length + 1;
  while (isStepNameDuplicate(`Step ${stepNumber}`, stepConfigurations)) {
    stepNumber++;
  }
  return `Step ${stepNumber}`;
}

/**
 * Returns the display name for a step inserted from a Step Library template: the template's label, or the label with
 * the first free ` (N)` suffix from 2 when a step already uses it. Uses the same case- and whitespace-insensitive
 * comparison as rename, so a hand-typed `RCC | RMLO (2)` is skipped rather than duplicated. Terminates because the set
 * of taken names is finite.
 */
export function generateTemplateStepName(
  label: string,
  stepConfigurations: StepConfiguration[]
): string {
  if (!isStepNameDuplicate(label, stepConfigurations)) return label;

  let suffix = 2;
  while (isStepNameDuplicate(`${label} (${suffix})`, stepConfigurations)) {
    suffix++;
  }
  return `${label} (${suffix})`;
}

/** Identity of the study/series/stack a viewport resolved to. */
type ResolvedSeries = Pick<ViewportConfigurationEntry, 'study_smid' | 'series_smid' | 'stack_smid'>;

/** Result of {@link preserveResolvedViewports}, carrying counts so callers can log what happened. */
export type PreserveResolvedViewportsResult = {
  steps: StepConfiguration[];
  /** Viewports that regained a study/series/stack from the previous state. */
  reattached: number;
  /** Viewports that declare a matching rule but still resolve to nothing. These render blank. */
  unresolved: number;
};

/** True when the viewport has no resolved series to render. */
function isUnresolved(viewport: ViewportConfigurationEntry): boolean {
  return viewport.study_smid == null && viewport.series_smid == null && viewport.stack_smid == null;
}

/**
 * Stable key for the rule a viewport uses to pick a series: its criteria plus the source text and
 * comparison index. Two viewports with the same rule resolve to the same series by definition, so
 * the key is safe to share resolutions across — including the members of a mirror group, which
 * carry identical rules on purpose.
 */
function viewportRuleKey(viewport: ViewportConfigurationEntry): string | null {
  const criteria = (viewport.criteria ?? [])
    .map((c) => `${c.category}=${c.value}`)
    .sort()
    .join('|');
  const sourceText = viewport.series_criteria_source_text?.trim() ?? '';
  // A viewport with neither criteria nor source text has no rule to match on; leave it alone.
  if (criteria === '' && sourceText === '') return null;
  return JSON.stringify([sourceText, viewport.comparison_index ?? null, criteria]);
}

/**
 * Re-attaches server-resolved study/series/stack smids to step viewports that lost them.
 *
 * Hanging protocols are stored criteria-only: every viewport's study_smid, series_smid and
 * stack_smid are null in the database, and it is the server-side match at case load that resolves
 * them against the studies actually open. The HP edit form works in the stored shape, so the steps
 * it flushes on save carry nulls too. Writing those straight into the viewer atom replaces the
 * resolved assignments with nothing, and every viewport in the affected steps renders blank until
 * the page is refreshed and the match runs again (VX-4764).
 *
 * The refetch that follows the save does not rescue this: `useHangingProtocol`'s
 * `__lastInitializedHPSmid` guard deliberately refuses to re-initialize the steps for an HP it has
 * already seen, so the resolved copy never reaches the atom.
 *
 * So carry the resolution forward here instead. A viewport is only filled in when it resolves to
 * nothing AND its rule is byte-identical to one the previous state had already resolved — if the
 * user retargeted a viewport in the form, its rule changed, and it is correctly left unresolved for
 * the next match rather than pinned to the series it used to show.
 */
export function preserveResolvedViewports(
  flushedSteps: StepConfiguration[],
  previousSteps: Nullable<StepConfiguration[]>
): PreserveResolvedViewportsResult {
  const resolvedByRule = new Map<string, ResolvedSeries>();
  for (const step of previousSteps ?? []) {
    for (const viewport of Object.values(step.viewports ?? {})) {
      if (isUnresolved(viewport)) continue;
      const key = viewportRuleKey(viewport);
      if (key == null || resolvedByRule.has(key)) continue;
      resolvedByRule.set(key, {
        study_smid: viewport.study_smid ?? null,
        series_smid: viewport.series_smid ?? null,
        stack_smid: viewport.stack_smid ?? null,
      });
    }
  }

  let reattached = 0;
  let unresolved = 0;

  const steps = flushedSteps.map((step) => {
    let stepChanged = false;
    const viewports: Record<string, ViewportConfigurationEntry> = {};

    for (const [viewportId, viewport] of Object.entries(step.viewports ?? {})) {
      // An intentionally cleared viewport is meant to be empty; leave it that way.
      if (!isUnresolved(viewport) || viewport.is_clear === true) {
        viewports[viewportId] = viewport;
        continue;
      }

      const key = viewportRuleKey(viewport);
      const resolved = key == null ? undefined : resolvedByRule.get(key);
      if (resolved == null) {
        if (key != null) unresolved++;
        viewports[viewportId] = viewport;
        continue;
      }

      viewports[viewportId] = { ...viewport, ...resolved };
      reattached++;
      stepChanged = true;
    }

    return stepChanged ? { ...step, viewports } : step;
  });

  return { steps, reattached, unresolved };
}

/** What a step viewport's criteria look like on the wire: category/value pairs. */
type ViewportCriterion = { category: string; value: string };

/**
 * Fills in `criteria` for step viewports that hold a series but carry no classification.
 *
 * A viewport with no criteria is not a neutral one. The backend matcher tries classification
 * **first** and only falls back to description matching when classification matched nothing
 * (`_match_single_viewport_to_series`), so a criteria-less viewport is resolved by its description or
 * by the remaining-series fill -- and either can hand the reader a series they never chose. That is
 * VX-5125: on a two-window protocol, all four window-1 viewports and one window-0 viewport saved with
 * `criteria=[NONE]`, and `Viewport-0-0-1` came back hanging "ABD & PEL DELAY" where the reader had put
 * "Scout".
 *
 * They save empty because nothing classifies them. Criteria reach a viewport from one of two places,
 * and neither covers these:
 *
 * - the HP form's own `SeriesClassifierInput`, which only exists for the viewports the form
 *   *renders* -- and the form renders the primary window's grid only, so window-1 viewports are never
 *   classified at all;
 * - `useClinicalViewportClassification`, which writes through `applyServerCriteriaToViewport` into a
 *   *step*. On the Default HP there are no steps, so a clinical drag before the protocol exists has
 *   nowhere to record its classification either.
 *
 * So derive them here, from the series the viewport actually holds. This is VX-4970's rule -- the
 * series' own classification, not the classification of its description text -- applied to the
 * viewports that fix could not reach: the series record carries categories the words cannot
 * (`view_orientation`, `localizer`, `ct_kernel`), and those are the discriminating ones.
 *
 * Only ever *adds*. A viewport that already carries criteria is left exactly as it is, so this cannot
 * overwrite what the form's classifier or the reader's own edit decided; and a viewport whose series
 * is unknown, or whose series has no stored classification, is left empty rather than guessed at.
 */
export function fillMissingSeriesCriteria(
  steps: StepConfiguration[],
  {
    seriesIdForViewport,
    criteriaForSeries,
  }: {
    /** Which series a viewport of a given step holds, or undefined when nothing can answer. */
    seriesIdForViewport: (step: StepConfiguration, viewportId: string) => string | undefined;
    /** The series' own classification, flattened to category/value pairs. */
    criteriaForSeries: (seriesSmid: string) => ViewportCriterion[];
  }
): { steps: StepConfiguration[]; filled: number } {
  let filled = 0;

  const next = steps.map((step) => {
    let changed = false;
    const viewports: Record<string, ViewportConfigurationEntry> = {};

    for (const [viewportId, viewport] of Object.entries(step.viewports ?? {})) {
      const hasCriteria = (viewport.criteria ?? []).length > 0;
      const seriesSmid = hasCriteria ? undefined : seriesIdForViewport(step, viewportId);
      const criteria = seriesSmid == null ? [] : criteriaForSeries(seriesSmid);
      if (criteria.length === 0) {
        viewports[viewportId] = viewport;
        continue;
      }
      viewports[viewportId] = { ...viewport, criteria };
      filled++;
      changed = true;
    }

    return changed ? { ...step, viewports } : step;
  });

  return { steps: filled > 0 ? next : steps, filled };
}

/**
 * Adopts the backend's step smids after a whole-HP update.
 *
 * A PATCH that carries a `steps` array makes the backend delete and recreate every step row, so the
 * smids the client sent no longer exist and any later per-step delete or rename against them fails
 * with "No HangingProtocolStep matches the given query" (VX-4773). The response carries the new
 * smids plus an `order`, so pair them back up by order. Tracked for a server-side fix as VX-4741,
 * after which this becomes a no-op rather than a wrong answer.
 *
 * Returns `steps` untouched when the response is missing, a different length, or missing an `order`
 * on any entry, since a positional pairing would be a guess at that point and a wrong smid is worse
 * than a stale one. `order` is nullable on the schema, and entries without one would otherwise all
 * sort as 0 and pair against whatever position they happened to arrive in.
 */
export function reconcileStepSmids(
  steps: StepConfiguration[],
  returnedSteps: Nullable<ReadonlyArray<{ smid?: Nullable<string>; order?: Nullable<number> }>>
): StepConfiguration[] {
  if (returnedSteps == null || returnedSteps.length !== steps.length) return steps;
  if (returnedSteps.some((step) => step.order == null)) return steps;
  if (new Set(returnedSteps.map((step) => step.order)).size !== returnedSteps.length) return steps;

  const byOrder = [...returnedSteps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return steps.map((step, index) => {
    const smid = byOrder[index]?.smid;
    return smid != null && smid !== step.smid ? { ...step, smid } : step;
  });
}

/** A single step-level move: put `smid` at `order`, 0-based, letting the backend shift its siblings. */
export interface StepOrderMove {
  smid: string;
  order: number;
}

/**
 * Indices of a longest strictly increasing subsequence of `values`, via patience sorting with
 * predecessor links. Used to decide which steps a reorder can leave alone.
 */
function longestIncreasingSubsequenceIndices(values: readonly number[]): number[] {
  const predecessors = new Array<number>(values.length).fill(-1);
  // Index (into `values`) of the smallest tail of every subsequence length found so far.
  const tails: number[] = [];

  values.forEach((value, i) => {
    let lo = 0;
    let hi = tails.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (values[tails[mid]] < value) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0) predecessors[i] = tails[lo - 1];
    tails[lo] = i;
  });

  const result: number[] = [];
  let cursor = tails.length > 0 ? tails[tails.length - 1] : -1;
  while (cursor !== -1) {
    result.push(cursor);
    cursor = predecessors[cursor];
  }
  return result.reverse();
}

/**
 * Plans the per-step PATCHes that turn the server's current step order into the one the user just
 * dragged into place (VX-4765).
 *
 * The backend moves a step with `to()`, which shifts only the rows between its old and new position
 * and preserves everyone else's relative order (EN-12751). So the steps that already sit in the
 * right order relative to each other never need to be written: the longest such run is exactly a
 * longest increasing subsequence of their current positions, and moving only the rest around it
 * lands the target order. That keeps a drag at one PATCH whichever direction it went, which matters
 * because the endpoint serializes these on a row lock over the parent HP.
 *
 * Each move is planned against the order the earlier moves left behind, hence the projection.
 *
 * Steps the server does not know about (a client smid from an optimistic create that has not
 * resolved yet) are excluded from both sides: they hold no position in the server's list, so
 * counting them would offset every index after them.
 */
export function planStepOrderMoves(
  previousOrder: ReadonlyArray<Nullable<string>>,
  targetOrder: ReadonlyArray<Nullable<string>>
): StepOrderMove[] {
  const isPersisted = (smid: Nullable<string>): smid is string =>
    smid != null && !isClientGeneratedSmid(smid);

  const previous = previousOrder.filter(isPersisted);
  const target = targetOrder.filter(isPersisted).filter((smid) => previous.includes(smid));
  const projected = previous.filter((smid) => target.includes(smid));

  const stayPut = new Set(
    longestIncreasingSubsequenceIndices(target.map((smid) => projected.indexOf(smid))).map(
      (i) => target[i]
    )
  );

  const moves: StepOrderMove[] = [];
  target.forEach((smid, order) => {
    if (stayPut.has(smid)) return;
    const from = projected.indexOf(smid);
    if (from === order) return;
    moves.push({ smid, order });
    projected.splice(from, 1);
    projected.splice(order, 0, smid);
  });
  return moves;
}

/**
 * Puts `steps` back into `smidOrder`, for restoring the step bar after a rejected reorder (VX-4765).
 *
 * Reorders are serialized and planned against the order the drop started from, so a rejection means
 * the server still holds `smidOrder` and reasserting it locally is what re-converges the two.
 *
 * Returns null when the two no longer describe the same steps -- one created, deleted or resolved to
 * a different smid since the drop. Forcing a stale order onto a changed list would drop a step
 * outright, which is worse than the order being wrong, so the caller leaves the atom alone instead.
 * Steps are paired by smid, and entries with a null smid pair off in encounter order.
 */
export function restoreStepOrder(
  steps: Nullable<StepConfiguration[]>,
  smidOrder: ReadonlyArray<Nullable<string>>
): StepConfiguration[] | null {
  if (steps == null || steps.length !== smidOrder.length) return null;

  const remaining = [...steps];
  const restored: StepConfiguration[] = [];
  for (const smid of smidOrder) {
    const index = remaining.findIndex((step) => step.smid === smid);
    if (index === -1) return null;
    restored.push(remaining[index]);
    remaining.splice(index, 1);
  }
  return restored;
}

/**
 * Checks whether a step name is a duplicate within the given configurations, optionally excluding a
 * step by ID (useful when renaming).
 */
export function isStepNameDuplicate(
  name: string,
  stepConfigurations: StepConfiguration[],
  excludeStepId?: string
): boolean {
  const trimmed = name.trim().toLowerCase();
  // Resolve the index to exclude so we can handle both real smids and
  // synthetic `__index_N` identifiers used when smid is null.
  let excludeIndex = -1;
  if (excludeStepId != null) {
    const tempIndex = parseTempStepId(excludeStepId);
    if (tempIndex !== null) {
      excludeIndex = tempIndex;
    } else {
      excludeIndex = stepConfigurations.findIndex((s) => s.smid === excludeStepId);
    }
  }
  return stepConfigurations.some(
    (step, i) => i !== excludeIndex && (step.display_name ?? '').trim().toLowerCase() === trimmed
  );
}
